import { useAtom, useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { KBText, KBIcon, KBTooltip } from '@/components/atoms'
import { useOnboardingStatus, OnboardingStep } from '@/hooks/useOnboarding'
import { isTrialAtom } from '@/store/trial.atom'
import { hideOnboardingPanelAtom } from '@/store/onboarding.atom'
import { useAnalytics } from '@/hooks/useAnalytics'
import { cn } from '@/utils/tailwind.helper'

export interface OnboardingBadgeProps {
  /** Additional className */
  className?: string
  /** Compact mode - only shows icon, no text (for narrow sidebars) */
  compact?: boolean
  /** Use light theme colors (for dark backgrounds) */
  light?: boolean
}

/**
 * OnboardingBadge - Compact badge showing setup progress
 *
 * Shows in the sidebar when the onboarding panel is dismissed.
 * Displays a circular progress indicator and allows reopening the panel.
 */
const OnboardingBadge = ({
  className,
  compact = false,
  light = false,
}: OnboardingBadgeProps) => {
  const { t } = useTranslation()
  const { track } = useAnalytics()
  const isTrial = useAtomValue(isTrialAtom)
  const { data: status, isLoading } = useOnboardingStatus()
  const [isHidden, setIsHidden] = useAtom(hideOnboardingPanelAtom)

  // Don't show if not in trial, still loading, all complete, API-dismissed, or panel is visible
  if (
    !isTrial ||
    isLoading ||
    status?.isComplete ||
    status?.isDismissed ||
    !isHidden
  ) {
    return null
  }

  const steps = Object.values(OnboardingStep)
  const completedSteps = status?.completedSteps ?? 0
  const totalSteps = status?.totalSteps ?? steps.length
  const progressPercent = status?.progressPercent ?? 0

  const handleClick = () => {
    track('onboarding_badge.clicked', {
      completed_steps: completedSteps,
      total_steps: totalSteps,
    })
    setIsHidden(false) // Show the panel
  }

  // Calculate circle progress
  const radius = compact ? 12 : 10
  const size = compact ? 32 : 24
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset =
    circumference - (progressPercent / 100) * circumference

  // Colors based on light/dark theme
  const bgCircleColor = light ? 'rgba(255,255,255,0.3)' : '#E5E7EB'
  const progressColor = light ? '#FFFFFF' : '#6366F1'
  const iconColor = light ? 'tw-text-white' : 'tw-text-indigo-600'
  const textColor = light ? 'tw-text-white' : 'tw-text-gray-700'

  return (
    <KBTooltip
      title={t('onboarding.setupGuideProgress', {
        completed: completedSteps,
        total: totalSteps,
      })}
    >
      <div
        className={cn(
          'tw-flex tw-cursor-pointer tw-items-center tw-rounded-full tw-border tw-transition-colors',
          compact
            ? 'tw-justify-center tw-p-2'
            : 'tw-gap-3 tw-py-1 tw-pl-1 tw-pr-3',
          light
            ? 'tw-border-white/30 hover:tw-border-white/50 hover:tw-bg-white/10'
            : 'tw-border-gray-300 hover:tw-border-indigo-400 hover:tw-bg-indigo-50',
          className
        )}
        onClick={handleClick}
      >
        {/* Icon on the left - same size as avatar (32px to match user avatar) */}
        <div
          className={cn(
            'tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full',
            light ? 'tw-bg-white/20' : 'tw-bg-indigo-100',
            'tw-h-8 tw-w-8'
          )}
        >
          <KBIcon name="rocket" size={16} className={iconColor} />
        </div>

        {/* Label and progress - only show in non-compact mode */}
        {!compact && (
          <>
            <KBText
              className={cn('tw-flex-1 tw-text-sm tw-font-medium', textColor)}
            >
              {t('onboarding.setupGuide')}
            </KBText>

            {/* Circular progress indicator on the right */}
            <div className="tw-relative tw-h-6 tw-w-6 tw-flex-shrink-0">
              <svg
                className="tw-h-6 tw-w-6 -tw-rotate-90"
                viewBox={`0 0 ${size} ${size}`}
              >
                {/* Background circle */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={bgCircleColor}
                  strokeWidth={2.5}
                />
                {/* Progress circle */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={progressColor}
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="tw-transition-all tw-duration-300"
                />
              </svg>
            </div>
          </>
        )}
      </div>
    </KBTooltip>
  )
}

export default OnboardingBadge
